import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromApp from './store/app.reducer';
import * as AuthActions from './auth/store/auth.actions';
import { TranslateService } from '@ngx-translate/core';
import { delay, filter, startWith } from 'rxjs/operators';
import { registerLocaleData } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import { ActivatedRoute, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { LoadingService } from './service/loading-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { PosthogService } from './service/posthog.service';

registerLocaleData(localeSv, 'sv');
registerLocaleData(localeEn, 'en');
declare global {
    interface Window {
        clarity: any;
    }
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    title = 'fotbollpariktigt-web';
    message: any;
    loading$ = this.loadingService.loading$.pipe(
        startWith(false),
        delay(0)
    );
    routerLoading = true; // Track router navigation state
    private isInitialLoad = true; // Track if this is the first navigation

    constructor(
        private store: Store<fromApp.AppState>,
        private translate: TranslateService,
        private router: Router,
        private loadingService: LoadingService,
        private spinner: NgxSpinnerService,
        private cdRef: ChangeDetectorRef,
        private route: ActivatedRoute,
        private posthogService: PosthogService
    ) {
        // Suppress Google Maps deprecation warnings globally
        this.suppressGoogleMapsWarnings();

        // Start initial load - show global loader on page refresh
        this.loadingService.startInitialLoad();
        this.initializeAuth();
        this.initializeTranslation();
        this.subscribeToAuthState();

        // Track router navigation to show/hide loader (only on initial load)
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                // Only show loader on initial load, not on subsequent navigations
                if (this.isInitialLoad) {
                    this.routerLoading = true;
                }
            } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                // Track page view in PostHog
                if (event instanceof NavigationEnd) {
                    this.posthogService.trackPageView(event.urlAfterRedirects);
                }

                // Hide loader after first navigation completes
                if (this.isInitialLoad) {
                    // Small delay to ensure content is rendered
                    setTimeout(() => {
                        this.routerLoading = false;
                        this.isInitialLoad = false; // Mark initial load as complete
                        this.cdRef.detectChanges();
                    }, 100);
                }
            }
        });
    }

    ngOnInit() {
        // Show/hide global loader and ngx-spinner
        this.loading$.subscribe(isLoading => {
            const globalLoader = document.getElementById('global-loader');
            if (isLoading) {
                document.body.classList.add('loading');
                this.spinner.show();
                if (globalLoader) {
                    globalLoader.style.display = 'flex';
                }
            } else {
                document.body.classList.remove('loading');
                this.spinner.hide();
                if (globalLoader) {
                    globalLoader.style.display = 'none';
                }
            }
        });

        // Complete initial load after a timeout as fallback (in case some APIs don't complete)
        setTimeout(() => {
            this.loadingService.completeInitialLoad();
        }, 5000); // 5 second fallback

        // Hide router loader after initial navigation (if it hasn't been hidden by router events)
        setTimeout(() => {
            if (this.routerLoading && this.isInitialLoad) {
                this.routerLoading = false;
                this.isInitialLoad = false; // Mark initial load as complete
                this.cdRef.detectChanges();
            }
        }, 2000); // 2 second fallback
    }

    ngAfterViewInit() {
        this.cdRef.detectChanges();
    }

    private initializeAuth() {
        if (localStorage.getItem('jwt')) {
            this.store.dispatch(new AuthActions.Autologin());
        }
    }

    private initializeTranslation() {
        this.translate.setDefaultLang('sv');
        const locale = localStorage.getItem('locale');
        if (locale) {
            this.translate.use(locale);
        } else {
            this.translate.use('sv');
            localStorage.setItem('locale', 'sv');
        }
    }

    private subscribeToAuthState() {
        this.store.select(state => state.auth).pipe(
            filter(authState => authState.customer != null && authState.employee != null)
        ).subscribe(authState => {
            // Once auth is complete, wait a bit for other initial APIs, then complete initial load
            setTimeout(() => {
                this.loadingService.completeInitialLoad();
            }, 1000);
        });
    }

    private handleUnauthenticated(): void {
        // Log the user out and remove items from localStorage
        this.store.dispatch(new AuthActions.Logout());
        localStorage.removeItem('jwt');
        localStorage.removeItem('tokenExpiration');

        // Define public routes (no full reload when unauthenticated on these)
        const publicRoutes = [
            '/login',
            '/register',
            '/forgot-password',
            '/password-reset-success',
            '/reset-password-link',
            '/download',
            '/',
            '/reset-password',
            '/components/assignments'  // allow guests to stay without reload (e.g. when switching language)
        ];

        // Get the current route path (without query params)
        const currentUrl = this.router.url.split('?')[0]; // Remove query params from URL for route comparison
        if (!publicRoutes.includes(currentUrl) && currentUrl !== '/reset-password') {
            window.location.reload();
        } else if (currentUrl === '/reset-password') {
            const token = this.route.snapshot.queryParams['token'];  // Get token from query params
            if (token) {
                return;
            }
        }
    }


    // Handle click outside main wrapper to check authentication.
    // Use app-root so the entire app content (router-outlet + routed views) is considered "inside";
    // router-outlet alone does not contain the routed component DOM (it's a sibling in Angular).
    @HostListener('document:click', ['$event'])
    handleClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        const mainWrapper = document.querySelector('app-root');
        if (mainWrapper && !mainWrapper.contains(target)) {
            if (!localStorage.getItem('jwt')) {
                this.handleUnauthenticated();
                return;
            }
        }
    }

    // Suppress Google Maps deprecation warnings globally
    private suppressGoogleMapsWarnings(): void {
        const originalWarn = console.warn.bind(console);
        const originalError = console.error.bind(console);

        const isGoogleMapsDeprecation = (args: any[]): boolean => {
            const message = args.map(arg => String(arg)).join(' ');
            return message.includes('google.maps.places.Autocomplete') ||
                message.includes('google.maps.places.PlaceAutocompleteElement') ||
                message.includes('google.maps.places.AutocompleteService') ||
                message.includes('google.maps.Marker') ||
                message.includes('utc_offset') ||
                message.includes('As of March 1st, 2025') ||
                message.includes('As of February 21st, 2024') ||
                message.includes('As of November 2019') ||
                (message.includes('deprecated') && message.includes('google'));
        };
    }
}